/**
 * #670 — pure presentation of an AutomationWindDownResult. No hooks, no
 * fetches: the capture harness renders this directly with fixtures; the
 * panel owns all state. Presentation derives from configured /
 * desiredStateChanged / detail-class — NOT status alone — so a no-op org
 * never reads as "we stopped automation" and missing credentials is a
 * distinct actionable state (adversarial-review blockers 2 and 3).
 */
import {
  AUTOMATION_FAMILIES,
  MISSING_CREDENTIALS_SNIPPET,
  QUIESCENCE_TIMEOUT_SNIPPET,
  type AutomationWindDownFamilyResult,
  type AutomationWindDownResult,
} from '../../types/automationWindDown';

const FAMILY_LABEL = {
  marty: 'Marty / vanilla day automation',
  rita: 'Rita orders',
  triggers: 'Server-side trigger runs',
} as const;

const TONE = {
  neutral: 'border-border bg-muted/30 text-muted-foreground',
  good: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400',
  warn: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  bad: 'border-red-500/40 bg-red-500/10 text-red-400',
} as const;

export function familyPresentation(fam: AutomationWindDownFamilyResult): {
  label: string;
  tone: string;
  next: string | null;
} {
  switch (fam.status) {
    case 'drained':
      if (!fam.configured) return { label: 'Not configured', tone: TONE.neutral, next: null };
      if (!fam.desiredStateChanged) return { label: 'Already stopped', tone: TONE.good, next: null };
      return { label: 'Drained', tone: TONE.good, next: null };
    case 'residue':
      return {
        label: 'Residue',
        tone: TONE.warn,
        next: 'Cancel the remaining orders manually from the org account, then retry to re-verify.',
      };
    case 'unverifiable': {
      if (fam.detail.includes(MISSING_CREDENTIALS_SNIPPET)) {
        return {
          label: 'Cannot be verified',
          tone: TONE.bad,
          next: 'This organization has no Kalshi credentials stored, so nothing could be read or cancelled at the venue. Add org credentials on this page, then run the wind-down again — retrying before that will give the same answer.',
        };
      }
      if (fam.family === 'triggers') {
        return {
          label: 'Cannot be verified',
          tone: TONE.bad,
          next: 'A trigger run could not be recovered automatically. Use the Trigger recovery panel above, then re-run to verify.',
        };
      }
      if (fam.detail.includes(QUIESCENCE_TIMEOUT_SNIPPET)) {
        return {
          label: 'Cannot be verified',
          tone: TONE.bad,
          next: 'An automation placement was still in flight when verification ran. Re-run to verify — do not assume the book is clean.',
        };
      }
      return {
        label: 'Cannot be verified',
        tone: TONE.bad,
        next: 'The venue could not be read. Verify at the venue before trusting any state here, or retry.',
      };
    }
    case 'failed':
      return {
        label: 'Failed',
        tone: TONE.bad,
        next: 'This family did not wind down. Fix the reported cause, then retry.',
      };
  }
}

function overallPresentation(result: AutomationWindDownResult): { label: string; tone: string } {
  const fams = AUTOMATION_FAMILIES.map((f) => result.families[f]);
  if (result.status === 'complete') {
    const nothingArmed = fams.every((f) => !f.desiredStateChanged && f.targetOrderIds.length === 0);
    return nothingArmed
      ? { label: 'Complete — nothing was running; no automation to stop', tone: TONE.good }
      : { label: 'Complete — every family drained', tone: TONE.good };
  }
  if (result.status === 'partial') {
    return { label: 'Partial — automation is NOT confirmed stopped', tone: TONE.warn };
  }
  return { label: 'Failed — no family wound down', tone: TONE.bad };
}

function OrderIdList({ label, ids, densityId }: { label: string; ids: string[]; densityId: string }) {
  if (ids.length === 0) return null;
  return (
    <div className="mt-1.5" data-density-id={densityId}>
      <div className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
        {label} ({ids.length})
      </div>
      <ul className="bg-muted/30 mt-1 flex max-h-32 flex-wrap gap-1 overflow-y-auto rounded p-1.5">
        {ids.map((id) => (
          <li key={id} className="bg-muted rounded px-1 py-0.5 font-mono text-[10px] break-all">
            {id}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Raw server text: visually subordinate, bounded, native-foldaway (<details>
 *  needs no state, so no lint-budget cost). Our copy always leads; this never
 *  replaces it. Triggers' detail is a "; "-joined runId list — split to rows. */
function ServerDetail({ fam }: { fam: AutomationWindDownFamilyResult }) {
  if (!fam.detail) return null;
  const rows = fam.family === 'triggers' && fam.detail.includes('; ')
    ? fam.detail.split('; ')
    : [fam.detail];
  return (
    <details className="mt-1">
      <summary className="text-muted-foreground cursor-pointer text-[10px] font-semibold uppercase tracking-wider">
        Server detail
      </summary>
      <div className="bg-muted/30 mt-1 max-h-24 overflow-y-auto rounded p-1.5">
        {rows.map((row, i) => (
          <div key={i} className="text-muted-foreground font-mono text-[10px] break-all">
            {row.slice(0, 500)}
          </div>
        ))}
      </div>
    </details>
  );
}

function FamilyRow({ fam }: { fam: AutomationWindDownFamilyResult }) {
  const p = familyPresentation(fam);
  return (
    <div className="border-border rounded border p-2" data-density-id={`winddown-family-${fam.family}`}>
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 truncate text-xs font-semibold">{FAMILY_LABEL[fam.family]}</span>
        <span className={`shrink-0 rounded border px-2 py-0.5 text-[10px] font-bold ${p.tone}`}>{p.label}</span>
      </div>
      {p.next && <p className="mt-1 text-[11px]">{p.next}</p>}
      <OrderIdList
        label="Targeted for cancellation — no longer open unless also listed below"
        ids={fam.targetOrderIds}
        densityId={`winddown-target-${fam.family}`}
      />
      <OrderIdList label="Remaining targeted orders" ids={fam.remainingOrderIds} densityId={`winddown-remaining-${fam.family}`} />
      <OrderIdList
        label="Open automation-namespace orders not targeted"
        ids={fam.unclassifiedOrderIds}
        densityId={`winddown-unclassified-${fam.family}`}
      />
      <ServerDetail fam={fam} />
    </div>
  );
}

export function AutomationWindDownResultCard({
  result,
  targetName,
  previous = false,
}: {
  result: AutomationWindDownResult;
  targetName: string;
  previous?: boolean;
}) {
  const overall = overallPresentation(result);
  const okCancels = result.orphanCancels.filter((o) => o.ok);
  const failedCancels = result.orphanCancels.filter((o) => !o.ok);
  const durationS = Math.max(0, (result.finishedAt - result.startedAt) / 1000).toFixed(1);
  return (
    <div className={`mt-3 ${previous ? 'opacity-80' : ''}`} data-density-id="winddown-result">
      {previous && (
        <div className="text-muted-foreground mb-1 text-[10px] font-semibold uppercase tracking-wider">
          Previous attempt — kept so the residue and audit ID are not lost
        </div>
      )}
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className={`shrink-0 rounded border px-2 py-0.5 text-[10px] font-bold ${overall.tone}`}>
          {overall.label}
        </span>
        <span className="text-muted-foreground min-w-0 truncate text-xs">
          Result for <strong>{targetName}</strong> · {durationS}s
        </span>
      </div>
      <div className="text-muted-foreground mt-1 text-[11px]" data-density-id="winddown-audit">
        Audit ID <span className="font-mono select-all">{result.operationId}</span> — keep this for the
        durable audit record.
      </div>
      <div className="mt-2 space-y-2">
        {AUTOMATION_FAMILIES.map((f) => (
          <FamilyRow key={f} fam={result.families[f]} />
        ))}
      </div>
      {result.orphanCancels.length > 0 && (
        <div className="mt-2 rounded-md bg-muted/30 px-3 py-2 text-[11px]" data-density-id="winddown-orphans">
          <div className="font-semibold">
            Recovery cancels: {okCancels.length} succeeded, {failedCancels.length} failed
          </div>
          {failedCancels.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-red-400">
              {failedCancels.map((o) => (
                <li key={o.orderId} className="font-mono break-all">
                  {o.orderId} — {o.error ?? 'unknown error'}
                </li>
              ))}
            </ul>
          )}
          {okCancels.length > 0 && (
            <ul className="mt-1 max-h-24 space-y-0.5 overflow-y-auto">
              {okCancels.map((o) => (
                <li key={o.orderId} className="text-muted-foreground font-mono break-all">
                  {o.orderId} — cancelled or already gone
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
